import { IA_MODEL } from '@educabot/educablocks-boards';
import Tutorial1Step1 from '@sections/creabots/components/steps/Tutorial1Step1';
import AIVerifyLearningTutorial2Step20 from '@sections/creabots/components/steps/AIVerifyLearningTutorial2Step20';
import {
  BLOCK_CATEGORY_TYPE,
  BOARD_ID_TYPE,
  CODE_CHECK_TYPE,
  COMMON_CLASSES,
  AI_PROJECTS_TYPE,
  KIT_NAME,
  MODAL_TYPE,
  Project,
  TRAIN_STEPS,
  DEFAULT_CANVAS_DISPLACEMENT_X,
} from '@sections/creabots/types/kits';
import * as routes from '@educabot/educablocks-cosmos';
import { getAIConfigForBlock, getAIElementForBlock } from '../config';
import TutorialSlider from '@sections/creabots/components/steps/TutorialSlider';
import { getTutorialImagesInfo } from '@sections/creabots/features/utils/getTutorialImagesInfo';
import { redColorRegex, greenColorRegex } from '@sections/creabots/features/utils/commonRegexs';


const intlPrefix = 'creabots.tutorials.ai.projects.justDance';
const imgBaseUrl = '/images/creabots/ai/projects/justDance';

export const justDance: Project = {
  id: 2,
  name: `${intlPrefix}.name`,
  description: `${intlPrefix}.description`,
  image: `${routes.bloquesUri}${imgBaseUrl}/project_image.png`, //missing image
  boardId: BOARD_ID_TYPE.AI,
  iaModelType: IA_MODEL.POSES,
  tutorial: [
    // Step 1.
    {
      step: {
        stepId: 0,
        modal: MODAL_TYPE.FULL,
        component: Tutorial1Step1,
        videoUrl: 'https://storage.googleapis.com/educabot-aec-cdn-1/labs/just-dance.mp4',
      },
    },
    // Step 2.
    {
      step: {
        stepId: 1,
        modal: MODAL_TYPE.FULL,
        component: TutorialSlider,
        images: getTutorialImagesInfo({
          kitName: KIT_NAME.AI,
          projectName: AI_PROJECTS_TYPE.JUST_DANCE,
          totalSteps: 11,
        }),
      },
    },
    // Step 3.
    {
      step: {
        stepId: 2,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step3`,
        image: `${imgBaseUrl}/steps/step3.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => /arriba|up|cima/i.test(obj.samples.class0?.name?.toLowerCase() || ''),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#mlClass-0',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 4.
    {
      step: {
        stepId: 3,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step4`,
        image: `${imgBaseUrl}/steps/step4.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samplesModalActive === 'class0',
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-takeSample-0',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 5.
    {
      step: {
        stepId: 4,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step5`,
        image: `${imgBaseUrl}/steps/step5.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 0,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 6.
    {
      step: {
        stepId: 5,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step6`,
        image: `${imgBaseUrl}/steps/step6.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 1,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 7.
    {
      step: {
        stepId: 6,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step7`,
        image: `${imgBaseUrl}/steps/step7.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 2,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 8.
    {
      step: {
        stepId: 7,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step8`,
        image: `${imgBaseUrl}/steps/step8.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class0?.image?.length > 3,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 9.
    {
      step: {
        stepId: 8,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step9`,
        image: `${imgBaseUrl}/steps/step9.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => !obj.samplesModalActive,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#load-samples-btn',
            xOffset: 50,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 10.
    {
      step: {
        stepId: 9,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step10`,
        image: `${imgBaseUrl}/steps/step10.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => Object.keys(obj.samples).includes('class1'),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#add-class-btn',
            xOffset: 130,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 11.
    {
      step: {
        stepId: 10,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step11`,
        image: `${imgBaseUrl}/steps/step11.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => /izquierda|left|esquerda/i.test(obj.samples.class1?.name?.toLowerCase() || ''),
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#mlClass-1',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 12.
    {
      step: {
        stepId: 11,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step12`,
        image: `${imgBaseUrl}/steps/step12.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samplesModalActive === 'class1',
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-takeSample-1',
            xOffset: 20,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 13.
    {
      step: {
        stepId: 12,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step13`,
        image: `${imgBaseUrl}/steps/step13.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 0,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 14.
    {
      step: {
        stepId: 13,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step14`,
        image: `${imgBaseUrl}/steps/step14.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 1,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 15.
    {
      step: {
        stepId: 14,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step15`,
        image: `${imgBaseUrl}/steps/step15.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 2,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 16.
    {
      step: {
        stepId: 15,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step16`,
        image: `${imgBaseUrl}/steps/step16.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.samples.class1?.image?.length > 3,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#capture-btn',
            xOffset: 0,
            yOffset: 0,
          },
        },
      ],
    },
    // Step 17.
    {
      step: {
        stepId: 16,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step17`,
        image: `${imgBaseUrl}/steps/step17.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => !obj.samplesModalActive,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#load-samples-btn',
            xOffset: 50,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 18.
    {
      step: {
        stepId: 17,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step18`,
        image: `${imgBaseUrl}/steps/step18.png`,
        iaStep: TRAIN_STEPS.TEACH,
        completionCode: (obj) => obj.trainingStep === TRAIN_STEPS.TRAIN,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-train',
            xOffset: 15,
            yOffset: -20,
          },
        },
      ],
    },
    // Step 19.
    {
      step: {
        stepId: 18,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step19`,
        image: `${imgBaseUrl}/steps/step19.png`,
        iaStep: TRAIN_STEPS.TRAIN,
        completionCode: (obj) => obj.modelTrained === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#train-btn',
            xOffset: -3,
            yOffset: -12,
          },
        },
      ],
    },
    // Step 20.
    {
      step: {
        stepId: 19,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step20`,
        image: `${imgBaseUrl}/steps/step20.png`,
        iaStep: TRAIN_STEPS.TRAIN,
        completionCode: (obj) => obj.trainingStep === TRAIN_STEPS.CLASSIFY,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#ml-analyze',
            xOffset: 15,
            yOffset: -20,
          },
        },
      ],
    },
    {
      step: {
        stepId: 19,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step20`,
        image: `${imgBaseUrl}/steps/step20.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.isClassifying === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#analyze-btn',
            xOffset: 135,
            yOffset: 145,
          },
        },
      ],
    },
    // Step 21.
    {
      step: {
        stepId: 20,
        modal: MODAL_TYPE.DOCKED,
        iaStep: TRAIN_STEPS.CLASSIFY,
        component: AIVerifyLearningTutorial2Step20,
      },
    },
    // Step 22.
    {
      step: {
        stepId: 21,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step22`,
        image: `${imgBaseUrl}/steps/step22.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.active === false,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#toggleBlocks',
            xOffset: -15,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 23.
    {
      step: {
        stepId: 22,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step23`,
        image: `${imgBaseUrl}/steps/step23.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 1, { x: 190, ignoreBlock: true }),
        completionCodeCheck: CODE_CHECK_TYPE.VARIABLE,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.ADD_VARIABLE_BTN_CLASS,
            xOffset: 40,
            yOffset: -18,
          },
        },
      ],
    },
    {
      step: {
        stepId: 22,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step23`,
        image: `${imgBaseUrl}/steps/step23.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 1),
        completionCode: 'voidsetup\\(\\)\\{\\D+\\w*=;[^}]*\\/\\/global_var_\\D+\\w*',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 45,
            yOffset: 20,
          },
        },
      ],
    },
    {
      step: {
        stepId: 22,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step23`,
        image: `${imgBaseUrl}/steps/step23.png`,
        completionCode: 'int\\D+\\w*;.*voidsetup\\(\\)\\{\\D+\\w*=;[^}]*\\/\\/global_var_\\D+\\w*',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 340,
            yOffset: 20,
          },
        },
      ],
    },
    {
      step: {
        stepId: 22,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step23`,
        image: `${imgBaseUrl}/steps/step23.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MATH, 0),
        completionCode: 'int\\D+\\w*;.*voidsetup\\(\\)\\{\\D+\\w*=0;[^}]*\\/\\/global_var_\\D+\\w*',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MATH, 0),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 420,
            yOffset: 20,
          },
        },
      ],
    },
    // Step 24.
    {
      step: {
        stepId: 23,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step24`,
        image: `${imgBaseUrl}/steps/step24.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        completionCode: 'if\\(command=="MLClass0"\\)\\{if\\(0==0\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 120,
          },
        },
      ],
    },
    // Step 25.
    {
      step: {
        stepId: 24,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step25`,
        image: `${imgBaseUrl}/steps/step25.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        completionCode: 'if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==0\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 193,
            yOffset: 135,
          },
        },
      ],
    },
    {
      step: {
        stepId: 24,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step25`,
        image: `${imgBaseUrl}/steps/step25.png`,
        completionCode: 'if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 370,
            yOffset: 135,
          },
        },
      ],
    },
    // Step 26.
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        completionCode: 'if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 160,
            yOffset: 168,
          },
        },
      ],
    },
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 330,
            yOffset: 182,
          },
        },
      ],
    },
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 367,
            yOffset: 182,
          },
        },
      ],
    },
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.show\\(\\);`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 406,
            yOffset: 182,
          },
        },
      ],
    },
    {
      step: {
        stepId: 25,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step26`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass0"\\)\\{if\\(([a-zA-Z_][\\w]*)==1\\)\\{SEMAFORO_3.clear\\(\\);SEMAFORO_3.setPixelColor\\(0,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(1,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(2,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.show\\(\\);`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 600,
            yOffset: 182,
          },
        },
      ],
    },
    // Step 27.
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{\\}\\}elseif\\(command=="MLClass1"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 140,
            yOffset: 225,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        completionCode: 'delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLClass1"\\)\\{',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 160,
            yOffset: 260,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 330,
            yOffset: 268,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 367,
            yOffset: 268,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLClass1"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 406,
            yOffset: 268,
          },
        },
      ],
    },
    {
      step: {
        stepId: 26,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step27`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_3.clear\\(\\);SEMAFORO_3.setPixelColor\\(0,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(1,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(2,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLClass1"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 600,
            yOffset: 268,
          },
        },
      ],
    },
    // Step 28.
    {
      step: {
        stepId: 27,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step28`,
        image: `${imgBaseUrl}/steps/step24.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        completionCode: 'if\\(command=="MLClass1"\\)\\{if\\(0==0\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 150,
            yOffset: 375,
          },
        },
      ],
    },
    // Step 29.
    {
      step: {
        stepId: 28,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step29`,
        image: `${imgBaseUrl}/steps/step29.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        completionCode: 'if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==0\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -100 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 193,
            yOffset: 387,
          },
        },
      ],
    },
    {
      step: {
        stepId: 28,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step29`,
        image: `${imgBaseUrl}/steps/step29.png`,
        completionCode: 'if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -100 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,

            xOffset: 370,
            yOffset: 385,
          },
        },
      ],
    },
    // Step 30.
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step26.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        completionCode: 'if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -120 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 160,
            yOffset: 420,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -120 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 330,
            yOffset: 434,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step29`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -120 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 367,
            yOffset: 434,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(${greenColorRegex}\\)\\);SEMAFORO_A0.show\\(\\);`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -120 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 406,
            yOffset: 434,
          },
        },
      ],
    },
    {
      step: {
        stepId: 29,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step30`,
        image: `${imgBaseUrl}/steps/step26.png`,
        completionCode: `if\\(command=="MLClass1"\\)\\{if\\(([a-zA-Z_][\\w]*)==2\\)\\{SEMAFORO_3.clear\\(\\);SEMAFORO_3.setPixelColor\\(0,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(1,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(2,SEMAFORO_3.Color\\(${greenColorRegex}\\)\\);SEMAFORO_3.show\\(\\);`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -120 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 600,
            yOffset: 434,
          },
        },
      ],
    },
    // Step 31.
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{\\}\\}elseif\\(command=="MLC_Stop"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -120 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 140,
            yOffset: 470,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        completionCode: 'delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(51,51,153\\)\\);SEMAFORO_A0.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLC_Stop"\\)\\{',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -180 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.OUTPUTS, 7),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 160,
            yOffset: 505,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -220 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 330,
            yOffset: 518,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -220 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 367,
            yOffset: 518,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_A0.clear\\(\\);SEMAFORO_A0.setPixelColor\\(0,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(1,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.setPixelColor\\(2,SEMAFORO_A0.Color\\(${redColorRegex}\\)\\);SEMAFORO_A0.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLC_Stop"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -220 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 406,
            yOffset: 518,
          },
        },
      ],
    },
    {
      step: {
        stepId: 30,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step31`,
        image: `${imgBaseUrl}/steps/step27.png`,
        completionCode: `delayMicroseconds\\(300\\);\\}else\\{SEMAFORO_3.clear\\(\\);SEMAFORO_3.setPixelColor\\(0,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(1,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.setPixelColor\\(2,SEMAFORO_3.Color\\(${redColorRegex}\\)\\);SEMAFORO_3.show\\(\\);delayMicroseconds\\(300\\);\\}\\}elseif\\(command=="MLC_Stop"\\)\\{`,
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -220 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 600,
            yOffset: 518,
          },
        },
      ],
    },
    // Step 32.
    {
      step: {
        stepId: 31,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step32`,
        image: `${imgBaseUrl}/steps/step32.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 0),
        completionCode: 'elseif\\(command=="MLC_Stop"\\)\\{\\D+\\w*=;\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -260 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 0),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 147,
            yOffset: 628,
          },
        },
      ],
    },
    {
      step: {
        stepId: 31,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step32`,
        image: `${imgBaseUrl}/steps/step32.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MATH, 0),
        completionCode: 'elseif\\(command=="MLC_Stop"\\)\\{\\D+\\w*=0;\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -260 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MATH, 0),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 250,
            yOffset: 632,
          },
        },
      ],
    },
    // Step 33.
    {
      step: {
        stepId: 32,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step33`,
        image: `${imgBaseUrl}/steps/step24.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(0==0\\)\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -300 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.CONTROL, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 45,
            yOffset: 688,
          },
        },
      ],
    },
    // Step 34.
    {
      step: {
        stepId: 33,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step34`,
        image: `${imgBaseUrl}/steps/step34.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -300 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 92,
            yOffset: 654,
          },
        },
      ],
    },
    // Step 35.
    {
      step: {
        stepId: 34,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step35`,
        image: `${imgBaseUrl}/steps/step35.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 0),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=;\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -340 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 0),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 60,
            yOffset: 691,
          },
        },
      ],
    },
    {
      step: {
        stepId: 34,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step35`,
        image: `${imgBaseUrl}/steps/step35.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.MATH, 3),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,11\\);\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -340 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.MATH, 3),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 160,
            yOffset: 697,
          },
        },
      ],
    },
    {
      step: {
        stepId: 34,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step35`,
        image: `${imgBaseUrl}/steps/step35.png`,
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,3\\);\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -340 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 322,
            yOffset: 697,
          },
        },
      ],
    },
    // Step 36.
    {
      step: {
        stepId: 35,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step36`,
        image: `${imgBaseUrl}/steps/step36.gif`,
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,3\\);\\}else\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -340 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 38,
            yOffset: 737,
          },
        },
      ],
    },
    {
      step: {
        stepId: 35,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step36`,
        image: `${imgBaseUrl}/steps/step36.gif`,
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,3\\);\\}elseif\\(false\\)\\{\\}else\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -380 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 38,
            yOffset: 791,
          },
        },
      ],
    },
    // Step 37.
    {
      step: {
        stepId: 36,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step37`,
        image: `${imgBaseUrl}/steps/step25.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.LOGIC, 0),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,3\\);\\}elseif\\(0==1\\)\\{\\}else\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -400 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.LOGIC, 0),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 98,
            yOffset: 737,
          },
        },
      ],
    },
    {
      step: {
        stepId: 36,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step37`,
        image: `${imgBaseUrl}/steps/step25.png`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        completionCode: '\\}else\\{buffer\\+=command;\\}\\}if\\(([a-zA-Z_][\\w]*)==0\\)\\{([a-zA-Z_][\\w]*)=random\\(1,3\\);\\}elseif\\(([a-zA-Z_][\\w]*)==1\\)\\{\\}else\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -400 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.VARIABLES, 3),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 118,
            yOffset: 737,
          },
        },
      ],
    },
    // Step 38.
    {
      step: {
        stepId: 37,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step38`,
        image: `${imgBaseUrl}/steps/step38.gif`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.DISPLAYS, 1),
        completionCode: 'random\\(1,3\\);\\}elseif\\(([a-zA-Z_][\\w]*)==1\\)\\{matrix_8x8.clear\\(\\);[^}]*\\}else\\{\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -440 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.DISPLAYS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 58,
            yOffset: 771,
          },
        },
      ],
    },
    {
      step: {
        stepId: 37,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step38`,
        image: `${imgBaseUrl}/steps/step38.gif`,
        completionCode: 'staticuint8_tbitmapMatrix\\[\\]=\\{[^}]*1+[^}]*\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -540 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 105,
            yOffset: 844,
          },
        },
      ],
    },
    // Step 39.
    {
      step: {
        stepId: 38,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step39`,
        image: `${imgBaseUrl}/steps/step39.gif`,
        ...getAIConfigForBlock(BLOCK_CATEGORY_TYPE.DISPLAYS, 1),
        completionCode: 'random\\(1,3\\);\\}elseif\\(([a-zA-Z_][\\w]*)==1\\)\\{matrix_8x8.clear\\(\\);[^}]*\\}else\\{matrix_8x8.clear\\(\\);[^}]*\\}\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: DEFAULT_CANVAS_DISPLACEMENT_X, y: -680 },
      },
      hints: [
        {
          element: getAIElementForBlock(BLOCK_CATEGORY_TYPE.DISPLAYS, 1),
        },
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 58,
            yOffset: 969,
          },
        },
      ],
    },
    {
      step: {
        stepId: 38,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step39`,
        image: `${imgBaseUrl}/steps/step39.gif`,
        completionCode: 'staticuint8_tbitmapMatrix\\[\\]=\\{[^}]*1+[^}]*\\};staticuint8_tbitmapMatrix\\[\\]=\\{[^}]*1+[^}]*\\}',
        completionCodeCheck: CODE_CHECK_TYPE.REGEX,
        canvasDisplacement: { x: 0, y: -700 },
      },
      hints: [
        {
          element: {
            class: COMMON_CLASSES.INITIAL_BLOCK_CLASS,
            xOffset: 105,
            yOffset: 1039,
          },
        },
      ],
    },
    // Step 40.
    {
      step: {
        stepId: 39,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step40`,
        image: `${imgBaseUrl}/steps/step40.gif`,
        completionCodeCheck: CODE_CHECK_TYPE.BUILT,
      },
      hints: [
        {
          element: {
            image: `${routes.bloquesUri}/images/creabots/hint_big.png`,
            class: COMMON_CLASSES.BUILD_BLOCKLY_BTN_CLASS,
            xOffset: -26,
            yOffset: -35,
          },
        },
      ],
    },
    // Step 41.
    {
      step: {
        stepId: 40,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step41`,
        image: `${imgBaseUrl}/steps/step41.png`,
        completionCode: (obj) => obj.active === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#toggleAI',
            xOffset: -15,
            yOffset: -15,
          },
        },
      ],
    },
    // Step 42.
    {
      step: {
        stepId: 41,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step42`,
        image: `${imgBaseUrl}/steps/step42.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.isClassifying === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#analyze-btn',
            xOffset: 132,
            yOffset: 142,
          },
        },
      ],
    },
    {
      step: {
        stepId: 41,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step42`,
        image: `${imgBaseUrl}/steps/step42.png`,
        iaStep: TRAIN_STEPS.CLASSIFY,
        completionCode: (obj) => obj.bluetooth.connected === true,
        completionCodeCheck: CODE_CHECK_TYPE.OBJECT_CONDITION,
      },
      hints: [
        {
          element: {
            class: '#MLConnectBluetooth',
            xOffset: 60,
            yOffset: -10,
          },
        },
      ],
    },
    // Step 43.
    {
      step: {
        stepId: 42,
        modal: MODAL_TYPE.DOCKED,
        description: `${intlPrefix}.step43`,
        image: `${imgBaseUrl}/steps/step43.gif`,
        iaStep: TRAIN_STEPS.CLASSIFY,
      },
    },

  ],
};

/**
 * AI RULES:
 *
 * (obj) => obj.samples.class0?.image?.length > 0
 * (obj) => Object.values(obj.samples).some(sample => Array.isArray(sample.image) && sample.image?.length > 1)
 * (obj) => Object.values(obj.samples).every(sample => Array.isArray(sample.image) && sample.image?.length > 1)
 * (obj) => Object.values(obj.samples).some((s: any) => s.name && s.name.toLowerCase() === 'palma')
 * (obj) => obj.modelTrained === true
 */
